import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserCog, faExclamationTriangle, faCheckCircle } from '@fortawesome/free-solid-svg-icons';

/**
 * CgBenchmarkBadge - Displays a colored badge indicating the benchmark source type
 *
 * Sources:
 * - actual: Verified accurate benchmark for this CN good (primary color)
 * - custom: Organization's custom benchmark (green)
 * - default: Default/generic benchmark value (orange-yellow)
 */
const CgBenchmarkBadge = ({ benchmarkSource, size = 'sm', showIcon = true, className = '' }) => {
  const { t } = useTranslation('common');
  // Configuration for each benchmark source type
  const sourceConfig = {
    custom: {
      label: t('common:benchmark.sourceCustom', 'Custom'),
      icon: faUserCog,
      bgColor: 'bg-green-100',
      textColor: 'text-green-700',
      borderColor: 'border-green-200',
    },
    default: {
      label: t('common:benchmark.sourceDefault', 'Default'),
      icon: faExclamationTriangle,
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-700',
      borderColor: 'border-amber-200',
    },
    // Legacy alias - "system" maps to the same styling as "default"
    system: {
      label: t('common:benchmark.sourceDefault', 'Default'),
      icon: faExclamationTriangle,
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-700',
      borderColor: 'border-amber-200',
    },
    actual: {
      label: t('common:benchmark.sourceActual', 'Actual'),
      icon: faCheckCircle,
      bgColor: 'bg-primaryColor/10',
      textColor: 'text-primaryColor',
      borderColor: 'border-primaryColor/20',
    },
  };

  // Size configurations
  const sizeConfig = {
    sm: {
      padding: 'px-1.5 py-0.5',
      text: 'text-xs',
      iconSize: 'text-[10px]',
      gap: 'gap-1',
    },
    md: {
      padding: 'px-2 py-1',
      text: 'text-sm',
      iconSize: 'text-xs',
      gap: 'gap-1.5',
    },
    lg: {
      padding: 'px-3 py-1.5',
      text: 'text-base',
      iconSize: 'text-sm',
      gap: 'gap-2',
    },
  };

  // Get config for the current source, fallback to default
  const config = sourceConfig[benchmarkSource] || sourceConfig.default;
  const sizeStyles = sizeConfig[size] || sizeConfig.sm;

  return (
    <span
      className={`
        inline-flex items-center ${sizeStyles.gap}
        ${sizeStyles.padding} ${sizeStyles.text}
        ${config.bgColor} ${config.textColor}
        border ${config.borderColor}
        rounded-full font-medium
        whitespace-nowrap
        ${className}
      `}
    >
      {showIcon && (
        <FontAwesomeIcon
          icon={config.icon}
          className={sizeStyles.iconSize}
        />
      )}
      <span>{config.label}</span>
    </span>
  );
};

CgBenchmarkBadge.propTypes = {
  /** The source of the benchmark: 'custom', 'default', 'actual', or 'system' (legacy alias for default) */
  benchmarkSource: PropTypes.oneOf(['custom', 'default', 'actual', 'system']),
  /** Size of the badge */
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  /** Whether to show the icon */
  showIcon: PropTypes.bool,
  /** Additional CSS classes */
  className: PropTypes.string,
};

CgBenchmarkBadge.defaultProps = {
  benchmarkSource: 'default',
  size: 'sm',
  showIcon: true,
  className: '',
};

export default CgBenchmarkBadge;
